/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/authentication/components/ProtectedRoute.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/authentication/components/ProtectedRoute.tsx
 * filename: ProtectedRoute.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { PropsWithChildren, useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/platform/authentication/hooks/useAuth';
import { evaluateRouteAccess, subjectForUser } from '@/app/config/navPermissions';
import { capabilityPathFromDestination } from '@/app/navigation/routes/capabilities/destinationCapabilityPaths';
import { clearReturnTo, locationToPath, rememberReturnTo } from '@/platform/auth/returnTo';

function AccessDenied({ requiredPermission }: { requiredPermission: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[rgb(var(--color-bg-primary))] p-6">
      <section className="w-full max-w-lg rounded-2xl border border-[rgb(var(--color-border-primary))] bg-[rgb(var(--color-bg-secondary))] p-8 text-center shadow-lg">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--color-text-tertiary))]">
          403
        </p>
        <h1 className="mt-3 text-2xl font-semibold text-[rgb(var(--color-text-primary))]">
          Access denied
        </h1>
        <p className="mt-3 text-sm text-[rgb(var(--color-text-secondary))]">
          Your assigned roles do not grant <code>{requiredPermission}</code> for this route.
        </p>
        <Link
          className="mt-6 inline-flex rounded-lg bg-[rgb(var(--color-accent-primary))] px-4 py-2 text-sm font-semibold text-white"
          to="/workbench"
        >
          Return to Workbench
        </Link>
      </section>
    </main>
  );
}

export function ProtectedRoute({ children }: PropsWithChildren) {
  const { user, isAuthenticated, isLoading, token, mfaRequired, mfaEnroll, pinRequired } =
    useAuth();
  const location = useLocation();
  const fullyIn = !isLoading && isAuthenticated && !(token && (mfaRequired || mfaEnroll || pinRequired));

  // Once a protected page renders for a fully signed-in user, the sign-in
  // journey is over — drop any stored return path so it can't hijack a
  // later sign-in.
  useEffect(() => {
    if (fullyIn) clearReturnTo();
  }, [fullyIn]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[rgb(var(--color-bg-primary))]">
        <div className="flex flex-col items-center gap-4">
          <div className="spinner-lg" />
          <p className="text-[rgb(var(--color-text-secondary))] text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // If we have a token but MFA is required/enrollment needed, route through
  // auth-v2 surfaces. Existing-user verify → /login?step=challenge; new-user
  // enrolment → /first-time.
  // Every detour off a protected page (challenge, enrolment, PIN, sign-in)
  // keeps the full destination — in router state for this load and in
  // sessionStorage for the trips that reload or leave the SPA.
  const detour = (to: string) => {
    rememberReturnTo(locationToPath(location));
    return <Navigate to={to} state={{ from: location }} replace />;
  };

  if (token && mfaRequired) {
    return detour('/login?step=challenge');
  }
  if (token && mfaEnroll) {
    return detour('/first-time');
  }

  // If MFA is done but PIN needs setup, redirect to PIN page
  if (token && pinRequired && location.pathname !== '/mfa/pin-setup') {
    return detour('/mfa/pin-setup');
  }

  if (!isAuthenticated) {
    return detour('/login');
  }

  const policyPath = capabilityPathFromDestination(location.pathname) ?? location.pathname;
  const decision = evaluateRouteAccess(subjectForUser(user), policyPath);
  if (!decision.allowed) {
    return <AccessDenied requiredPermission={decision.requiredPermission} />;
  }

  // Setup completeness is shown via SetupBanner — users are never blocked from navigating
  return <>{children}</>;
}
